import {useCallback} from 'react';
import {FormInput, FormSelect, FormTextarea} from '../FormElements';
import {Field, Form} from 'react-final-form';
import Button from '../Button';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import * as Validation from '../../utils/form-validation';
import {sendEmails} from '../../utils/api';
import axios from 'axios';
import IconClose from '../../assets/images/icon-email-sent.svg';
import {FORM_ERROR} from 'final-form';

type Props = {
  onCancel: () => void;
  project: HRProject;
  jwtToken: string;
};

type FormValues = {
  subject: string;
  emails: string;
  lang: 'en' | 'it' | 'es' | 'pl';
  message?: string;
};

const LANG_MAP = {
  it: 'It',
  en: 'En',
  es: 'Es',
  pl: 'Pl',
};

const EmailForm = ({onCancel, project, jwtToken}: Props) => {
  const {t, i18n} = useTranslation();

  const userLang = i18n.language;

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await sendEmails({
          jwtToken,
          token: project.token,
          body: {
            subject: values.subject,
            emails: values.emails,
            lang: values.lang,
            message: values.message,
          },
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data) {
          if (err.response.data.emails) {
            const emails = err.response.data.emails as string[];

            const message = emails.reduce(
              (acc, cur) => acc.trim() + '\n' + cur.trim()
            );

            return {
              [FORM_ERROR]: t('errors.form-error'),
              ...err.response.data,
              emails: message,
            };
          }

          return {
            [FORM_ERROR]: t('errors.form-error'),
            ...err.response.data,
          };
        }

        return {[FORM_ERROR]: t('errors.generic-error')};
      }
    },
    [project.token, jwtToken, t]
  );

  const link = `https://play.game2value.it/join/${project.token}`;

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          lang: userLang,
        }}
        render={({
          handleSubmit,
          submitting,
          values,
          submitSucceeded,
          submitError,
          submitFailed,
        }) => {
          const gameText =
            // @ts-ignore
            project.game[`invitationEmail${LANG_MAP[values.lang]}`];

          if (submitSucceeded) {
            return (
              <Intro>
                <h2>{t('dashboard.email.title')}</h2>
                <Succeeded>
                  <img src={IconClose} alt="close" />{' '}
                  {t('dashboard.email.succeeded')}
                </Succeeded>
              </Intro>
            );
          }

          return (
            <>
              <Intro>
                <h2>{t('dashboard.email.title')}</h2>
                <p
                  dangerouslySetInnerHTML={{__html: t('dashboard.email.intro')}}
                />
              </Intro>
              <FormWrapper>
                <form onSubmit={handleSubmit}>
                  <HalfWidth>
                    <Field
                      name="lang"
                      label={t('dashboard.email.label-languages')}
                      component={FormSelect}
                      validate={Validation.required}
                      options={[
                        {
                          label: t('language.it'),
                          value: 'it',
                        },
                        {
                          label: t('language.en'),
                          value: 'en',
                        },
                        {
                          label: t('language.es'),
                          value: 'es',
                        },
                        {
                          label: t('language.pl'),
                          value: 'pl',
                        },
                      ]}
                    />
                  </HalfWidth>

                  <Field
                    name="subject"
                    label={t('dashboard.email.label-subject')}
                    component={FormInput}
                    initialValue={t('dashboard.email.placeholder-subject', {
                      lng: values.lang,
                    })}
                    validate={Validation.required}
                  />

                  <Field
                    name="emails"
                    label={t('dashboard.email.label-to')}
                    component={FormTextarea}
                    placeholder="name@email.it, name@email.it..."
                    validate={Validation.required}
                    sublabel={t('dashboard.email.sublabel-to')}
                  />

                  <EmailPreview>
                    <p>
                      {t('dashboard.email.label-hello', {
                        lng: values.lang,
                      })}
                    </p>
                    <Field
                      name="message"
                      label={t('dashboard.email.label-free-text')}
                      component={FormTextarea}
                      placeholder={t('dashboard.email.placeholder-free-text')}
                    />

                    {gameText && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: gameText,
                        }}
                      ></p>
                    )}

                    <p>
                      {t('dashboard.email.use-link', {
                        lng: values.lang,
                      })}
                      <br />
                      <a href={link}>{link}</a>
                    </p>
                    <p>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('dashboard.email.regards', {
                            lng: values.lang,
                          }),
                        }}
                      ></span>
                    </p>
                    <p>
                      <i>
                        <strong>{project.customer.name}</strong>
                      </i>
                    </p>
                  </EmailPreview>

                  {submitFailed && !submitError && (
                    <Error>{t('errors.form-check-fields')}</Error>
                  )}

                  {submitError && <Error>{submitError}</Error>}

                  <Buttons>
                    <Button outline type="button" onClick={onCancel}>
                      {t('dashboard.email.cancel')}
                    </Button>
                    <Button loading={submitting} type="submit">
                      {t('dashboard.email.send')}
                    </Button>
                  </Buttons>
                </form>
              </FormWrapper>
            </>
          );
        }}
      />
    </>
  );
};

export default EmailForm;

const Intro = styled.div`
  h2 {
    color: ${(p) => p.theme.colors.violet500};
    font-weight: 700;
    font-size: 1.75rem;
    font-family: 'Mulish', sans-serif;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.125rem;
    line-height: 1.5;
  }
`;

const FormWrapper = styled.div`
  margin-top: 2.5rem;
`;

const EmailPreview = styled.div`
  position: relative;
  border-radius: 1rem;
  padding: 2.5rem 1.5rem;
  background-color: ${(p) => p.theme.colors.violet100};

  & > * + * {
    margin-top: 1rem;
  }

  a {
    word-break: break-all;
    color: ${(p) => p.theme.colors.violet500};
    font-weight: 500;
  }

  p {
    font-size: 1.125rem;
    line-height: 1.5;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 3.5rem;
`;

const HalfWidth = styled.div`
  width: 50%;

  @media ${(props) => props.theme.breakpoints.tablet} {
    width: 100%;
  }
`;

const Error = styled.p`
  margin-top: 24px;
  padding: 0 1rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 1.1rem;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.red};
`;

const Succeeded = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
